import { FC, useRef } from 'react';
import {
  Button,
  Flex,
  Heading,
  Stack,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
} from '@chakra-ui/react';

import { MdNoteAdd } from 'react-icons/md';
import { washHub } from 'store/slices';
import { UserPill } from 'components/core/UserPill';
import { Filters } from 'components/primitives/Filters';
import { SearchInput } from 'components/primitives/SearchInput';
import { AddHarvestSummaryModal } from 'components/modules/AddHarvestSummaryModal';
import InReviewSummary from './InReviewSummary';
import ReadyForImpactSummary from './ReadyForImpacts';
import DoneSummary from './DoneSummary';
import {
  HarvestSummaryViewProps,
  Tabs as TabNames,
} from './HarvestSummary.props';
import AllHarvestSummary from './AllHarvestSummary';
import ArchivedHarvestSummary from './ArchivedHarvestSummary';
import ReadyForInvoiceSummary from './ReadyForInvoice';
import { DateRangeInputExt } from 'components/modules/DateRangeInputExt';
import { InventoryReportStatusEnum } from 'types/report';

const HarvestSummaryView: FC<HarvestSummaryViewProps> = (props) => {
  const {
    counts,
    isLoading,
    pagination,
    isArchiveTab,
    status,
    tabIdx,
    sites,
    startDate,
    endDate,
    setSites,
    setAccountManagers,
    setSentToClient,
    sentToClient,
    feeAdjustmentTypes,
    setFeeAdjustmentTypes,
    setEndDate,
    setStartDate,
    onTabChange,
    sortHandler,
    reportCounts,
    searchInput,
    onSearchChange,
  } = props;
  const _selected = { backgroundColor: 'primary.900', color: 'support.500' };
  const _focus = { border: 'none', backgroundColor: 'primary.500' };
  const addHarvestSummaryModalRef = useRef<any>(null);
  const selectedWashHubId = washHub.selectors.useSelectedWashHubId();
  const onApplyFilters = (data: any, type: string) => {
    pagination.setPage(1);
    if (type === 'accountManagers') {
      setAccountManagers(data);
      return;
    }
    if (type === 'sentToClient') {
      setSentToClient(data);
      return;
    }
    if (type === 'feeAdjustmentTypes') {
      setFeeAdjustmentTypes(data);
      return;
    }
    setSites(data);
  };

  const onClearFilters = () => {
    pagination.setPage(1);

    setSites(undefined);
    setAccountManagers(undefined);
    setSentToClient(undefined);
    setFeeAdjustmentTypes(undefined);
  };

  const onSearchPress = (startDate: Date | null, endDate: Date | null) => {
    pagination.setPage(1);

    if (startDate) {
      setStartDate(startDate);
    }

    if (endDate) {
      setEndDate(endDate);
    }
  };

  const renderExpBtn = () => {
    return (
      <>
        <Button
          ml={2}
          minW="200px"
          onClick={async () => {
            props.mutateCsv();
          }}>
          Export to CSV
        </Button>
      </>
    );
  };

  const onSearchClear = () => {
    pagination.setPage(1);
    setStartDate(undefined);
    setEndDate(undefined);
  };

  const _props = {
    counts,
    isLoading,
    pagination,
    sortHandler,
  };
  const tabIndex = parseInt(window.location.hash.substring(1));
  const inReviewCount = reportCounts[InventoryReportStatusEnum.IN_REVIEW] || 0;
  const readyForImpactCount =
    reportCounts[InventoryReportStatusEnum.READY_FOR_IMPACT] || 0;
  const readyForInvoiceCount =
    reportCounts[InventoryReportStatusEnum.READY_FOR_INVOICE] || 0;
  const doneCount = reportCounts[InventoryReportStatusEnum.DONE] || 0;
  const allCount =
    inReviewCount + readyForImpactCount + readyForInvoiceCount + doneCount;

  return (
    <Flex flex={1} direction="column" color="support.500" overflow="auto">
      <Flex align="flex-start" justify="space-between">
        <Heading mb={6} fontSize="24px">
          Harvest Summary
        </Heading>
        <Flex align="flex-end">
          <Stack isInline>
            {renderExpBtn()}
            <Button
              isDisabled={selectedWashHubId === ''}
              mr={2}
              minW="200px"
              leftIcon={<MdNoteAdd size="20px" />}
              onClick={() => {
                addHarvestSummaryModalRef.current?.onOpen?.();
              }}>
              Add Harvest Summary
            </Button>
            <UserPill />
          </Stack>
        </Flex>
      </Flex>

      <Flex mt="20px" direction="column">
        <DateRangeInputExt
          startDate={startDate}
          endDate={endDate}
          onSearchClear={onSearchClear}
          onSearchPress={onSearchPress}
        />

        <Flex justify="space-between" align="flex-start" gap={2}>
          <Filters
            venue={sites}
            isSitesOnly
            isAccountManagersOnly
            isSentToClientOnly
            isFeeTypesOnly
            sentToClient={sentToClient}
            feeAdjustmentTypes={feeAdjustmentTypes}
            isFullButton
            onApplyFilters={onApplyFilters}
            onClearFilters={onClearFilters}
          />
          <SearchInput
            placeholder="Search harvest no., site, or name"
            value={searchInput}
            onChange={onSearchChange}
          />
        </Flex>
      </Flex>
      <Tabs
        onChange={(index: number) => onTabChange(index)}
        variant="enclosed"
        size="lg"
        index={tabIdx}>
        <TabList
          color="support.500"
          _selected={_selected}
          _focus={_focus}
          w="100%">
          <Tab
            _selected={_selected}
            px={12}
            py={3}
            fontSize="15px"
            fontWeight="bold">
            {`All (${allCount})`}
          </Tab>
          <Tab
            _selected={_selected}
            px={12}
            py={3}
            fontSize="15px"
            fontWeight="bold">
            {`In Review (${inReviewCount})`}
          </Tab>
          <Tab
            _selected={_selected}
            px={12}
            py={3}
            fontSize="15px"
            fontWeight="bold">
            {`Ready for Impacts (${readyForImpactCount})`}
          </Tab>
          <Tab
            _selected={_selected}
            px={12}
            py={3}
            fontSize="15px"
            fontWeight="bold">
            {`Ready for Invoice (${readyForInvoiceCount})`}
          </Tab>
          <Tab
            _selected={_selected}
            px={12}
            py={3}
            fontSize="15px"
            fontWeight="bold">
            {`Done (${doneCount})`}
          </Tab>
          <Tab
            _selected={_selected}
            px={12}
            py={3}
            fontSize="15px"
            fontWeight="bold">
            {`Archived (${reportCounts['archived'] || 0})`}
          </Tab>
        </TabList>
        <TabPanels>
          <TabPanel p={0}>
            <AllHarvestSummary {..._props} />
          </TabPanel>
          <TabPanel p={0}>
            <InReviewSummary {..._props} />
          </TabPanel>
          <TabPanel p={0}>
            <ReadyForImpactSummary {..._props} />
          </TabPanel>
          <TabPanel p={0}>
            <ReadyForInvoiceSummary {..._props} />
          </TabPanel>
          <TabPanel p={0}>
            <DoneSummary {..._props} />
          </TabPanel>
          <TabPanel p={0}>
            <ArchivedHarvestSummary {..._props} />
          </TabPanel>
        </TabPanels>
      </Tabs>
      <AddHarvestSummaryModal ref={addHarvestSummaryModalRef} />
    </Flex>
  );
};

export default HarvestSummaryView;
